import { useEffect, useRef } from "react";

// Same merchantId as the badge (frontend/src/components/YandexPayBadge.js).
const MERCHANT_ID = "40433079-69c8-4a63-a05f-95d89886e267";
// Matches cordstore.ru's own live configuration for this widget (verified by
// reading their yandex_pay_widget.js init call). Sandbox = no real money
// moves; this is a display/comparison calculator only. Switch to
// YaPay.PaymentEnv.PRODUCTION once real Split checkout is actually wired up.
const USE_SANDBOX = true;
// Same 19.5% non-cash markup as the badge - see YandexPayBadge.js for how
// this was verified against cordstore's live listings.
const SPLIT_PRICE_MULTIPLIER = 1.195;

let sdkPromise = null;
function loadYandexPaySdk() {
  if (window.YaPay) return Promise.resolve(window.YaPay);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://pay.yandex.ru/sdk/v1/pay.js";
      script.async = true;
      script.onload = () => resolve(window.YaPay);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return sdkPromise;
}

// The richer "Ultimate" payment widget - lets the shopper flip between
// Split's 2/4/6-month plans and see the per-payment amount for each, same as
// the block cordstore shows below their buy button. `amount` must be the
// site's cash price; the non-cash markup is applied here.
export default function YandexPaySplitWidget({ amount, className = "" }) {
  const ref = useRef(null);
  const sessionRef = useRef(null);
  const splitAmount = amount ? Math.round(amount * SPLIT_PRICE_MULTIPLIER * 100) / 100 : 0;

  useEffect(() => {
    if (!splitAmount || splitAmount <= 0) return;
    let cancelled = false;

    async function mount() {
      const YaPay = await loadYandexPaySdk();
      if (cancelled || !YaPay || !ref.current) return;

      if (sessionRef.current) {
        sessionRef.current.update(async () => ({ totalAmount: String(splitAmount) })).catch(() => {});
        return;
      }

      const paymentSession = await YaPay.createSession(
        {
          env: USE_SANDBOX ? YaPay.PaymentEnv.Sandbox : YaPay.PaymentEnv.PRODUCTION,
          version: 4,
          currencyCode: YaPay.CurrencyCode.Rub,
          merchantId: MERCHANT_ID,
          totalAmount: String(splitAmount),
          availablePaymentMethods: ["SPLIT"],
        },
        { source: "cms", onPayButtonClick: () => {} }
      );
      if (cancelled) return;
      sessionRef.current = paymentSession;
      paymentSession.mountWidget(ref.current, {
        widgetType: YaPay.WidgetType.Ultimate,
        widgetTheme: YaPay.WidgetTheme.Light,
        widgetBackground: YaPay.WidgetBackgroundType.Default,
        borderRadius: 8,
        withOutline: true,
        hideWidgetHeader: true,
      });
    }

    mount().catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitAmount]);

  useEffect(() => () => {
    sessionRef.current = null;
  }, []);

  if (!splitAmount || splitAmount <= 0) return null;
  return <div ref={ref} data-testid="yandex-pay-split-widget" className={className} />;
}
